import { useEffect, useState } from 'react';
import { FaCheck, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';

import { deleteGame, generateGames, getGames, getLeagues, getStandings, updateGame } from '../api';
import LeagueDisplay from '../components/LeagueDisplay';
import ResultsTable from '../components/ResultsTable';

function FixturesScreen() {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [scores, setScores] = useState({});

  // State for the delete confirmation dialog.
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  useEffect(() => {
    fetchLeagues();
  }, []);

  useEffect(() => {
    if (selectedLeagueId) {
      fetchGames();
      fetchStandings();
    }
  }, [selectedLeagueId]);

  const fetchLeagues = async () => {
    const response = await getLeagues();
    setLeagues(response.data);
    if (response.data.length > 0) {
      setSelectedLeagueId(response.data[0].id);
    }
  };

  const fetchGames = async () => {
    const response = await getGames(selectedLeagueId);
    setGames(response.data);
  };

  const fetchStandings = async () => {
    const response = await getStandings(selectedLeagueId);
    setStandings(response.data);
  };

  const handleGenerateGames = async () => {
    await generateGames(selectedLeagueId);
    fetchGames();
  };

  const handleScoreChange = (gameId, team, value) => {
    const existingScores = scores[gameId] || {};
    setScores({
      ...scores,
      [gameId]: {
        ...existingScores,
        [team]: value,
      },
    });
  };

  const handleScoreUpdate = async (gameId) => {
    const gameScores = scores[gameId] || {};
    const { home, away } = gameScores;
    await updateGame(gameId, { home_score: home, away_score: away });
    fetchGames();
    fetchStandings();
  };

  const handleDeleteResult = async (gameId) => {
    await updateGame(gameId, { home_score: null, away_score: null });
    fetchGames();
    fetchStandings();
  };

  const handleDeleteGame = async (game) => {
    setGameToDelete(game);
    setShowDeleteDialog(true);
  };

  const confirmDeleteGame = async () => {
    if (!gameToDelete) return;

    await deleteGame(gameToDelete.id);
    setShowDeleteDialog(false);
    setGameToDelete(null);
    fetchGames();
    fetchStandings();
  };

  const cancelDeleteGame = () => {
    setShowDeleteDialog(false);
    setGameToDelete(null);
  };
  
  const handleMove = async (gameId, direction) => {
    const gameIndex = games.findIndex(g => g.id === gameId);
    if (gameIndex === -1) return;

  
    const newIndex = gameIndex + direction;
    if (newIndex < 0 || newIndex >= games.length) return;
  
    const game = games[gameIndex];
    const otherGame = games[newIndex];
  

    // Swap order
    await updateGame(game.id, { order: otherGame.order });
    await updateGame(otherGame.id, { order: game.order });

  

    fetchGames();
  };

  const unplayedGames = games.filter((game) => game.home_score === null);
  const playedGames = games.filter((game) => game.home_score !== null).sort((a, b) => new Date(b.playedAt) - new Date(a.playedAt));

  return (
    <>
      {/* Delete Confirmation Dialog. */}
      {showDeleteDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Confirm Deletion</h3>
            <p>Warning: Are you sure you want to delete this fixture between <strong>{gameToDelete?.homeTeam.name}</strong> and <strong>{gameToDelete?.awayTeam.name}</strong>?</p>
            <p>There is no undo, and the only way to recreate this fixture is to delete the fixtures and results from this league and start again.</p>
            <div className="dialog-actions">
              <button onClick={cancelDeleteGame}>Cancel</button>
              <button onClick={confirmDeleteGame} className="danger">Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
      <div className="card-header">
        <h2>Fixtures</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
          <div className="select-container" style={{ minWidth: '200px' }}>
            <select value={selectedLeagueId} onChange={(e) => setSelectedLeagueId(e.target.value)}>
              {leagues.map((league) => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
          </div>
          {games.length === 0 && (
            <button onClick={handleGenerateGames}>Generate Fixtures</button>
          )}
        </div>
      </div>

      <LeagueDisplay standings={standings} />

      {unplayedGames.length > 0 && <h3>Fixtures</h3>}
      <table className="fixtures-table">
        <tbody>
          {unplayedGames.map((game) => (
            <tr key={game.id}>
              <td data-label="Home" className="mobile-hide">{game.homeTeam.name}</td>
              <td data-label="Match" className="mobile-show">
                <div className="mobile-stack">{game.homeTeam.name} vs {game.awayTeam.name}</div>
              </td>
              <td data-label="Home Score" className="mobile-hide">
                <input
                  type="number"
                  className="no-arrows"
                  style={{ width: '50px' }}
                  onChange={(e) => handleScoreChange(game.id, 'home', e.target.value)}
                />
              </td>
              <td className="mobile-hide">vs</td>
              <td data-label="Away Score" className="mobile-hide">
                <input
                  type="number"
                  className="no-arrows"
                  style={{ width: '50px' }}
                  onChange={(e) => handleScoreChange(game.id, 'away', e.target.value)}
                />
              </td>
              <td className="mobile-hide">{game.awayTeam.name}</td>
              <td data-label="Score" className="mobile-show">
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="number"
                    className="no-arrows"
                    style={{ width: '50px' }}
                    onChange={(e) => handleScoreChange(game.id, 'home', e.target.value)}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    className="no-arrows"
                    style={{ width: '50px' }}
                    onChange={(e) => handleScoreChange(game.id, 'away', e.target.value)}
                  />
                </div>
              </td>
              <td data-label="Actions">
                <div className="table-actions">
                  <button onClick={() => handleScoreUpdate(game.id)} className="submit icon-btn"><FaCheck /></button>
                  <button onClick={() => handleDeleteGame(game)} className="danger icon-btn"><FaTrash /></button>
                  <button onClick={() => handleMove(game.id, -1)} className="icon-btn" disabled={unplayedGames.findIndex(g => g.id === game.id) === 0}><FaArrowUp /></button>
                  <button onClick={() => handleMove(game.id, 1)} className="icon-btn" disabled={unplayedGames.findIndex(g => g.id === game.id) === unplayedGames.length - 1}><FaArrowDown /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {playedGames.length > 0 && <h3>Results</h3>}
      <ResultsTable games={playedGames} handleScoreChange={handleScoreChange} handleScoreUpdate={handleScoreUpdate} handleDelete={handleDeleteResult} />
      </div>
    </>
  );
}

export default FixturesScreen;
